import type { ComputedRef } from 'vue'
import type { ServiceSummaryStoryblok } from '@/storyblok/component-types-sb'

type RowIndex = string | number

type UseFareTableExpandableRowsParams = {
  services: ComputedRef<any[]>
  visibleFares: ComputedRef<any[]>
}

export default ({ services, visibleFares }: UseFareTableExpandableRowsParams) => {
  const expandedRows = ref<Record<string, boolean>>({})
  const isServiceFull = (service: any) => service?.full === true || service?.full === 'true'

  const getServiceKey = (service: any) => service?.service || service?.key

  const getRowId = (service: ServiceSummaryStoryblok, index: RowIndex) =>
    getServiceKey(service) || service?._uid || String(index)

  const findFareService = (fare: any, service: any) => {
    const serviceKey = getServiceKey(service)
    if (!serviceKey || !Array.isArray(fare?.services)) return null
    return fare.services.find((item: any) => item?.key === serviceKey) || null
  }

  const hasExpandedContent = (service: ServiceSummaryStoryblok) =>
    visibleFares.value.some((fare: any) => !!findFareService(fare, service)?.expandedText)

  const isRowExpanded = (service: ServiceSummaryStoryblok, index: RowIndex) =>
    !!expandedRows.value[getRowId(service, index)]

  const toggleRowExpanded = (service: ServiceSummaryStoryblok, index: RowIndex) => {
    const rowId = getRowId(service, index)
    expandedRows.value[rowId] = !expandedRows.value[rowId]
  }

  const expandableRowIds = computed(() =>
    services.value
      .map((service: ServiceSummaryStoryblok, index: number) => ({ service, index }))
      .filter(({ service }) => !isServiceFull(service) && hasExpandedContent(service))
      .map(({ service, index }) => getRowId(service, index))
  )

  const areAllRowsExpanded = computed(
    () =>
      expandableRowIds.value.length > 0 && expandableRowIds.value.every((rowId: string) => !!expandedRows.value[rowId])
  )

  const setAllRowsExpanded = (nextValue?: boolean) => {
    const next = !!nextValue
    expandableRowIds.value.forEach((rowId: string) => {
      expandedRows.value[rowId] = next
    })
  }

  return {
    expandedRows,
    findFareService,
    hasExpandedContent,
    isRowExpanded,
    toggleRowExpanded,
    expandableRowIds,
    areAllRowsExpanded,
    setAllRowsExpanded
  }
}
